// The Sessions roster toolbar — same two-zone strip as the slots heatmap:
//
//   shape ────────────────────────────────┐     ┌───────────── filter
//   Mode  [Template|Week]                  │     │  Location ▾  Doctors ▾
//   Group [Doctor|Clinic]                  │     │  Reset
//   Week  ‹ 5 Jun → 11 Jun ›  Today        │     │
//
// Template = the recurring weekly rules; Week = the effective roster for one
// specific week with per-day overrides applied. The WeekPicker is shared with
// the slots heatmap; in Template mode it still labels the header dates (cheap
// context for monthly-rotation badges).

import {
  Building2,
  CalendarClock,
  CalendarCog,
  CalendarRange,
  FilterX,
  Stethoscope,
} from "lucide-react";
import { FacetFilter, SegmentedToggle } from "../facet-filter";
import { WeekPicker } from "../slots-heatmap/toolbar";
import { hashHue } from "../slots-heatmap/heatmap-utils";

const MODE_SEGMENTS = [
  {
    id: "template",
    label: "Template",
    icon: CalendarRange,
    title: "The recurring weekly rules — who works where, every week",
  },
  {
    id: "week",
    label: "Week",
    icon: CalendarCog,
    title: "The effective roster for the picked week, with per-day overrides",
  },
];

const GROUP_BY_MODES = [
  {
    id: "doctor",
    label: "Doctor",
    icon: Stethoscope,
    title: "One row per doctor",
  },
  {
    id: "tenant",
    label: "Clinic",
    icon: Building2,
    title: "One row per location",
  },
];

const tenantHueStyle = (id) => ({ background: `oklch(0.62 0.16 ${hashHue(id)})` });

function FieldLabel({ children }) {
  return (
    <span className="select-none text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </span>
  );
}

function VDivider() {
  return <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />;
}

export function SessionsToolbar({
  mode,
  onModeChange,
  groupBy,
  onGroupByChange,
  rangeLabel,
  weekStartDate,
  onPickDate,
  isCurrentWeek,
  onPrevWeek,
  onNextWeek,
  onToday,
  tenants = [],
  selectedTenantIds = [],
  onToggleTenant,
  onSelectAllTenants,
  onClearTenants,
  doctors = [],
  selectedDoctorIds = [],
  onToggleDoctor,
  onSelectAllDoctors,
  onClearDoctors,
}) {
  const tenantsNarrowed =
    tenants.length > 0 && selectedTenantIds.length !== tenants.length;
  const doctorsNarrowed =
    doctors.length > 0 && selectedDoctorIds.length !== doctors.length;
  const hasActiveFilters = tenantsNarrowed || doctorsNarrowed;

  const handleReset = () => {
    onSelectAllTenants?.();
    onSelectAllDoctors?.();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-border bg-muted/30 px-5 py-2.5">
      <div className="flex items-center gap-1.5">
        <FieldLabel>Mode</FieldLabel>
        <SegmentedToggle
          value={mode}
          segments={MODE_SEGMENTS}
          onChange={onModeChange}
          ariaLabel="Roster mode"
        />
      </div>

      <VDivider />

      <div className="flex items-center gap-1.5">
        <FieldLabel>Group</FieldLabel>
        <SegmentedToggle
          value={groupBy}
          segments={GROUP_BY_MODES}
          onChange={onGroupByChange}
          ariaLabel="Group by"
        />
      </div>

      <VDivider />

      <div className="flex items-center gap-1.5">
        <FieldLabel>Week</FieldLabel>
        <WeekPicker
          rangeLabel={rangeLabel}
          weekStartDate={weekStartDate}
          onPickDate={onPickDate}
          onPrevWeek={onPrevWeek}
          onNextWeek={onNextWeek}
        />
        {!isCurrentWeek ? (
          <button
            type="button"
            onClick={onToday}
            aria-label="Jump to the current week"
            title="Jump to the current week"
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-2 text-[12px] font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
          >
            <CalendarClock className="size-3.5" />
            Today
          </button>
        ) : null}
      </div>

      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <FacetFilter
          icon={Building2}
          label="Location"
          options={tenants}
          selectedIds={selectedTenantIds}
          onToggle={onToggleTenant}
          onSelectAll={onSelectAllTenants}
          onClear={onClearTenants}
          dotColor={(opt) => tenantHueStyle(opt.id).background}
          searchPlaceholder="Search locations…"
          align="end"
        />
        <FacetFilter
          icon={Stethoscope}
          label="Doctors"
          options={doctors}
          selectedIds={selectedDoctorIds}
          onToggle={onToggleDoctor}
          onSelectAll={onSelectAllDoctors}
          onClear={onClearDoctors}
          searchPlaceholder="Search doctors…"
          align="end"
        />

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={handleReset}
            title="Clear all filters"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FilterX className="size-3.5" />
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}
